/**
 * API DTOs + validation. The web app imports these types; the API imports the
 * zod schemas for request validation. We never expose Prisma models directly.
 */
import { z } from 'zod';
export const emailSchema = z.string().email().max(254);
export const passwordSchema = z.string().min(10).max(200);
export const registerSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: z.string().min(1).max(80).optional(),
});
export const loginSchema = z.object({ email: emailSchema, password: z.string().min(1).max(200) });
export const createProjectSchema = z.object({
    name: z.string().min(1).max(120),
    aspectRatio: z.enum(['9:16', '16:9', '1:1', '4:5', 'source']).default('9:16'),
    // Optional Google STT source-language code (BCP-47, e.g. 'hi-IN'). Persisted on
    // the project so transcription (and later retranscribes) use it as the primary.
    language: z.string().max(16).optional(),
});
// Retranscribe with an optional source-language override; when set it is stored
// on the project so the choice sticks for future runs.
export const retranscribeSchema = z.object({
    language: z.string().max(16).optional(),
});
export const renameProjectSchema = z.object({ name: z.string().min(1).max(120) });
export const requestUploadSchema = z.object({
    filename: z.string().min(1).max(255),
    contentType: z.string().min(1).max(120),
    sizeBytes: z.number().int().positive(),
    durationMs: z.number().finite().positive().optional(),
});
export const confirmUploadSchema = z.object({
    uploadId: z.string().min(1),
});
export const exportSettingsSchema = z.object({
    aspectRatio: z.enum(['9:16', '16:9', '1:1', '4:5', 'source']),
    fit: z.enum(['fit', 'fill', 'crop']).default('fill'),
    resolution: z.enum(['720p', '1080p']).default('1080p'),
    fps: z.number().int().min(15).max(60).default(30),
    watermark: z.boolean().default(false),
});
export const saveWordSchema = z.object({
    id: z.string().min(1).max(80),
    text: z.string().max(200),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
    emphasized: z.boolean().optional(),
    hidden: z.boolean().optional(),
    isFiller: z.boolean().optional(),
    colorOverride: z.string().max(32).optional(),
    sizeScale: z.number().min(0.1).max(5).optional(),
    bold: z.boolean().optional(),
    depthBehind: z.boolean().optional(),
    wordStyle: z.lazy(() => wordStyleSchema).nullable().optional(),
});
export const saveSegmentSchema = z.object({
    wordIds: z.array(z.string().min(1).max(80)).max(60),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
    /** Optional per-line style: a template id to render this line with. */
    templateId: z.string().min(1).max(64).nullish(),
});
/** A timeline transition - a visual effect over a time window. Placed anywhere
 *  on the timeline; preview + (future) export read the same list. */
export const transitionSchema = z.object({
    id: z.string().min(1).max(64),
    kind: z.enum(['fade', 'flash', 'zoomIn', 'zoomOut', 'slide']),
    startMs: z.number().int().min(0),
    durationMs: z.number().int().min(50).max(10000),
});
/** kMagic run request - an optional free-text steer for the AI art director. */
export const magicRequestSchema = z.object({
    instruction: z.string().max(2000).optional(),
});
/** A B-roll overlay clip. `storageKey` is the durable object key (the client
 *  uploads the media first); a presigned URL is resolved on read. */
export const brollSchema = z.object({
    id: z.string().min(1).max(64),
    kind: z.enum(['image', 'video']),
    name: z.string().max(200),
    storageKey: z.string().min(1).max(400),
    startMs: z.number().int().min(0),
    durationMs: z.number().int().min(100).max(600000),
    mode: z.enum(['cover', 'overlay']),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    scale: z.number().min(0.05).max(1),
    fit: z.enum(['cover', 'contain']),
});
/** Persisted audio settings - original track level/mute + an external (non-blob)
 *  music track (e.g. kMagic's stock pick). Uploaded blob music is session-only. */
export const audioSettingsSchema = z.object({
    originalVolume: z.number().min(0).max(1),
    originalMuted: z.boolean(),
    music: z
        .object({
        name: z.string().max(200),
        /** External URL (e.g. stock) - omitted when the file is stored by key. */
        url: z.string().max(2000).optional(),
        /** Durable object key for an uploaded track (presigned on read). */
        storageKey: z.string().max(400).optional(),
        volume: z.number().min(0).max(1),
        loop: z.boolean(),
        duck: z.boolean(),
        startMs: z.number().int().min(0).optional(),
        durationMs: z.number().int().min(0).optional(),
    })
        .nullable()
        .optional(),
});
/** A hook moment / power-word takeover - a rare (≤4/video), big, center-screen
 *  standalone text event with its OWN time window (can linger past the spoken
 *  word) that can hide the running captions. Distinct from per-line emphasis. */
const hookFillEnum = z.enum(['solid', 'shine', 'flow', 'wipe', 'metal', 'footage']);
const hookCaseEnum = z.enum(['upper', 'asis', 'lower']);
const hookMotionKind = z.enum([
    'none', 'fade', 'slideUp', 'slideDown', 'slideLeft', 'slideRight', 'zoomIn', 'zoomOut',
    'punch', 'shake', 'blur', 'mask', 'drop', 'spin', 'trackIn',
]);
const hookEasing = z.enum(['easeOut', 'easeInOut', 'linear', 'expo', 'back', 'spring', 'bounce']);
const hookPhase = (kind) => z.object({
    kind,
    durationMs: z.number().int().min(0).max(10000),
    distance: z.number().min(0).max(1),
    blur: z.number().min(0).max(1),
    easing: hookEasing,
    fade: z.boolean().optional(),
});
const hookMotionSchema = z.object({
    in: hookPhase(hookMotionKind),
    out: hookPhase(z.union([hookMotionKind, z.literal('mirror')])),
    split: z.enum(['block', 'line', 'word', 'char']),
    staggerMs: z.number().int().min(0).max(3000),
    idle: z.enum(['none', 'float', 'pulse', 'pushIn', 'drift', 'sway', 'shake']),
    idleAmount: z.number().min(0).max(1),
});
const colorStr = z.string().max(40);
const gradientSchema = z.object({ from: colorStr, to: colorStr, angleDeg: z.number().min(-360).max(360) });
const strokeSpecSchema = z.object({ color: colorStr, widthPx: z.number().min(0).max(40) });
const wordGlowSchema = z.object({ color: colorStr, blurPx: z.number().min(0).max(120) });
const wordFillEnum = z.enum(['solid', 'gradient', 'metal']);
/** A word's own motion: entrance when spoken + a hold motion. */
export const wordMotionSchema = z.object({
    in: hookPhase(hookMotionKind),
    hold: z.enum(['none', 'float', 'pulse', 'pushIn', 'drift', 'sway', 'shake']),
    holdAmount: z.number().min(0).max(1),
});
/** Per-word look + motion (studio inspector); null fields follow the caption. */
export const wordStyleSchema = z.object({
    fontFamily: z.string().max(80).nullable().optional(),
    fontWeight: z.number().int().min(100).max(1000).nullable().optional(),
    italic: z.boolean().nullable().optional(),
    fill: wordFillEnum.nullable().optional(),
    gradient: gradientSchema.nullable().optional(),
    stroke: strokeSpecSchema.nullable().optional(),
    glow: wordGlowSchema.nullable().optional(),
    highlightColor: colorStr.nullable().optional(),
    motion: wordMotionSchema.nullable().optional(),
    breakAfter: z.boolean().nullable().optional(),
});
const hookLineStyleSchema = z.object({
    fontFamily: z.string().max(80).optional(),
    fontWeight: z.number().int().min(100).max(1000).optional(),
    italic: z.boolean().optional(),
    size: z.number().min(0.1).max(4).optional(),
    color: z.string().max(40).optional(),
    fill: hookFillEnum.optional(),
    textCase: hookCaseEnum.optional(),
    strike: z.string().max(40).optional(),
    delayMs: z.number().int().min(0).max(600000).optional(),
});
export const hookMomentSchema = z.object({
    id: z.string().min(1).max(64),
    type: z.enum(['opening', 'power']),
    // Newlines split the hook into explicit, individually styled lines.
    text: z.string().min(1).max(300),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
    anchorWordId: z.string().min(1).max(80).optional(),
    hideCaptions: z.boolean(),
    anchor: z.enum(['center', 'upper', 'lower']),
    scale: z.number().min(0.3).max(4),
    color: z.string().max(40),
    bgColor: z.string().max(40).optional(),
    animation: z.enum(['zoomIn', 'punch', 'popShake', 'riseFade', 'none']),
    behind: z.boolean().optional(),
    offsetYNorm: z.number().min(-0.5).max(0.5).optional(),
    offsetXNorm: z.number().min(-0.5).max(0.5).optional(),
    align: z.enum(['center', 'left', 'right']).optional(),
    rotateDeg: z.number().min(-90).max(90).optional(),
    // Style (all optional): a hook with none set draws the clean default title.
    fontFamily: z.string().max(80).optional(),
    fontWeight: z.number().int().min(100).max(1000).optional(),
    italic: z.boolean().optional(),
    textCase: hookCaseEnum.optional(),
    tracking: z.number().min(-0.3).max(2).optional(),
    lineHeight: z.number().min(0.4).max(3).optional(),
    opacity: z.number().min(0).max(1).optional(),
    stroke: z.object({ color: z.string().max(40), width: z.number().min(0).max(0.5) }).optional(),
    gradient: z.object({ from: z.string().max(40), to: z.string().max(40), angleDeg: z.number().min(-360).max(360) }).optional(),
    glow: z.object({ color: z.string().max(40), blur: z.number().min(0).max(2) }).optional(),
    fill: hookFillEnum.optional(),
    texture: z
        .object({
        zoom: z.number().min(1).max(5),
        blur: z.number().min(0).max(1),
        brightness: z.number().min(-1).max(1),
        saturation: z.number().min(0).max(1),
        tint: z.string().max(40).optional(),
        tintAmount: z.number().min(0).max(1),
    })
        .optional(),
    lineStyles: z.array(hookLineStyleSchema.nullable()).max(16).optional(),
    // Motion (entrance / exit / cascade / hold) + head tracking.
    motion: hookMotionSchema.optional(),
    track: z.object({ strength: z.number().min(0).max(1), scale: z.boolean() }).optional(),
});
export const saveCaptionsSchema = z.object({
    templateId: z.string().min(1).max(64).optional(),
    styleOverrides: z.record(z.unknown()).optional(),
    placementOverride: z.record(z.unknown()).optional(),
    transitions: z.array(transitionSchema).max(200).optional(),
    brolls: z.array(brollSchema).max(100).optional(),
    hooks: z.array(hookMomentSchema).max(200).optional(),
    audio: audioSettingsSchema.optional(),
    /** Output aspect ratio for preview + export. Persisted on the project. */
    aspectRatio: z.enum(['9:16', '16:9', '1:1', '4:5', 'source']).optional(),
    words: z.array(saveWordSchema).max(20000),
    segments: z.array(saveSegmentSchema).max(5000),
    /** Optimistic concurrency: the revision the client edited. */
    baseRevision: z.number().int().optional(),
});
/** Request for the AI transcript-enhance pass. The client sends the words it
 *  wants captioned (current on-screen order); the server decides breaks + the
 *  one key word per group. Timing is never sent to the model. */
export const enhanceWordSchema = z.object({
    id: z.string().min(1).max(80),
    text: z.string().max(200),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
});
export const enhanceRequestSchema = z.object({
    words: z.array(enhanceWordSchema).min(1).max(20000),
    language: z.string().max(16).optional(),
});
/** Translate the transcript into a target language (incl. Hinglish). Word-
 *  aligned so the original timings are preserved. */
export const translateRequestSchema = z.object({
    words: z.array(enhanceWordSchema).min(1).max(20000),
    targetLanguage: z.string().min(1).max(40),
    sourceLanguage: z.string().max(16).optional(),
});
//# sourceMappingURL=dto.js.map